import { supabase } from './supabaseClient';

/**
 * Channels Admin API Functions
 * Direct Supabase queries for channel management in the admin panel.
 *
 * RLS LIMITATION: This client uses the anon key, so RLS policies apply.
 * The current "Anyone can view active channels" policy means admins can only
 * see channels where is_active = true. Inactive channels will not appear
 * in query results until an admin-specific RLS policy is added:
 *
 *   CREATE POLICY "Admins can view all channels"
 *     ON channels FOR SELECT
 *     USING (auth.uid() IN (SELECT uuid FROM users WHERE role = 'admin'));
 */

/**
 * Fetch all channels with creator info and pagination
 * @param {Object} filters - { channel_type, privacy, is_active, is_featured, search }
 * @param {number} limit - Number of channels per page
 * @param {number} offset - Offset for pagination
 */
export const getAllChannels = async (filters = {}, limit = 20, offset = 0) => {
  try {
    let query = supabase
      .from('channels')
      .select(`
        id,
        name,
        slug,
        description,
        cover_image,
        channel_type,
        privacy,
        location_city,
        location_region,
        tags,
        member_count,
        is_active,
        is_featured,
        is_platform_created,
        created_at,
        updated_at,
        users!channels_created_by_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply filters
    if (filters.channel_type) {
      query = query.eq('channel_type', filters.channel_type);
    }

    if (filters.privacy) {
      query = query.eq('privacy', filters.privacy);
    }

    if (filters.is_active !== '' && filters.is_active !== undefined && filters.is_active !== null) {
      query = query.eq('is_active', filters.is_active === 'true' || filters.is_active === true);
    }

    if (filters.is_featured !== '' && filters.is_featured !== undefined && filters.is_featured !== null) {
      query = query.eq('is_featured', filters.is_featured === 'true' || filters.is_featured === true);
    }

    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Get total count for pagination
    let countQuery = supabase
      .from('channels')
      .select('*', { count: 'exact', head: true });

    if (filters.channel_type) countQuery = countQuery.eq('channel_type', filters.channel_type);
    if (filters.privacy) countQuery = countQuery.eq('privacy', filters.privacy);
    if (filters.is_active !== '' && filters.is_active !== undefined && filters.is_active !== null) {
      countQuery = countQuery.eq('is_active', filters.is_active === 'true' || filters.is_active === true);
    }
    if (filters.search) {
      countQuery = countQuery.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    const { count: totalCount, error: countError } = await countQuery;
    if (countError) throw countError;

    return {
      channels: data || [],
      total: totalCount || 0,
      hasMore: (offset + limit) < (totalCount || 0)
    };
  } catch (error) {
    console.error('Error fetching channels:', error);
    throw error;
  }
};

/**
 * Get a single channel with full detail and creator info
 * @param {number} channelId - Channel ID
 */
export const getChannelDetail = async (channelId) => {
  try {
    const { data, error } = await supabase
      .from('channels')
      .select(`
        *,
        users!channels_created_by_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .eq('id', channelId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching channel detail:', error);
    throw error;
  }
};

/**
 * Update a channel's fields
 * @param {number} channelId - Channel ID
 * @param {Object} updates - Fields to update
 */
export const updateChannel = async (channelId, updates) => {
  try {
    const { data, error } = await supabase
      .from('channels')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', channelId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating channel:', error);
    throw error;
  }
};

/**
 * Get members of a channel with user info
 * @param {number} channelId - Channel ID
 * @param {number} limit - Number of members per page
 * @param {number} offset - Offset for pagination
 */
export const getChannelMembers = async (channelId, limit = 50, offset = 0) => {
  try {
    const { data, error, count } = await supabase
      .from('channel_members')
      .select(`
        id,
        channel_id,
        user_uuid,
        role,
        status,
        joined_at,
        users!channel_members_user_uuid_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `, { count: 'exact' })
      .eq('channel_id', channelId)
      .order('joined_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return { members: data || [], total: count || 0 };
  } catch (error) {
    console.error('Error fetching channel members:', error);
    throw error;
  }
};

/**
 * Get posts in a channel with author info
 * @param {number} channelId - Channel ID
 * @param {number} limit - Number of posts per page
 * @param {number} offset - Offset for pagination
 */
export const getChannelPosts = async (channelId, limit = 20, offset = 0) => {
  try {
    const { data, error, count } = await supabase
      .from('channel_posts')
      .select(`
        id,
        channel_id,
        user_uuid,
        content,
        post_type,
        pinned,
        is_active,
        created_at,
        updated_at,
        users!channel_posts_user_uuid_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        ),
        channel_post_media ( id, position, media_type, media_url, vc_id )
      `, { count: 'exact' })
      .eq('channel_id', channelId)
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return {
      posts: data || [],
      total: count || 0,
      hasMore: (offset + limit) < (count || 0)
    };
  } catch (error) {
    console.error('Error fetching channel posts:', error);
    throw error;
  }
};

/**
 * Hide or unhide a channel post (moderation)
 * @param {number} postId - Post ID
 * @param {'hide'|'unhide'} action - Moderation action
 */
export const moderatePost = async (postId, action) => {
  try {
    const { data, error } = await supabase
      .from('channel_posts')
      .update({
        is_active: action === 'unhide',
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error moderating post:', error);
    throw error;
  }
};

/**
 * Set or unset featured status on a channel
 * @param {number} channelId - Channel ID
 * @param {boolean} featured - Whether to feature the channel
 */
export const featureChannel = async (channelId, featured) => {
  try {
    const { data, error } = await supabase
      .from('channels')
      .update({ is_featured: featured, updated_at: new Date().toISOString() })
      .eq('id', channelId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating channel featured status:', error);
    throw error;
  }
};

/**
 * Activate or deactivate a channel
 * @param {number} channelId - Channel ID
 * @param {boolean} isActive - Whether to activate the channel
 */
export const toggleChannelActive = async (channelId, isActive) => {
  try {
    const { data, error } = await supabase
      .from('channels')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', channelId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error toggling channel active status:', error);
    throw error;
  }
};

/**
 * Get aggregate analytics for the channels dashboard tab
 */
export const getChannelAnalytics = async () => {
  try {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const [
      { count: totalChannels },
      { count: activeChannels },
      { count: featuredChannels },
      { count: totalMembers },
      { count: totalPosts },
      { count: newChannels }
    ] = await Promise.all([
      supabase.from('channels').select('*', { count: 'exact', head: true }),
      supabase.from('channels').select('*', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('channels').select('*', { count: 'exact', head: true }).eq('is_featured', true),
      supabase.from('channel_members').select('*', { count: 'exact', head: true }),
      supabase.from('channel_posts').select('*', { count: 'exact', head: true }),
      supabase.from('channels').select('*', { count: 'exact', head: true }).gte('created_at', sevenDaysAgo)
    ]);

    return {
      totalChannels: totalChannels || 0,
      activeChannels: activeChannels || 0,
      featuredChannels: featuredChannels || 0,
      totalMembers: totalMembers || 0,
      totalPosts: totalPosts || 0,
      newChannels: newChannels || 0
    };
  } catch (error) {
    console.error('Error fetching channel analytics:', error);
    throw error;
  }
};

/**
 * Create a new channel from the admin panel (skips creator eligibility checks)
 * @param {Object} channelData - Channel fields
 * @param {string} channelData.name - Channel name (required)
 * @param {string} channelData.created_by - UUID of the owner user (required)
 * @param {string} [channelData.description]
 * @param {string} [channelData.channel_type] - 'interest' | 'location' | 'organization'
 * @param {string} [channelData.privacy] - 'public' | 'private' | 'invite_only'
 * @param {string} [channelData.cover_image] - Cloudinary URL
 * @param {string} [channelData.location_city]
 * @param {string} [channelData.location_region]
 * @param {string[]} [channelData.tags]
 * @param {number} [channelData.organization_id]
 * @param {boolean} [channelData.is_featured]
 * @param {boolean} [channelData.is_platform_created]
 */
export const createChannel = async (channelData) => {
  try {
    // Generate slug via existing DB function
    const { data: slug, error: slugError } = await supabase
      .rpc('generate_channel_slug', { p_name: channelData.name });

    if (slugError) throw slugError;

    // Insert channel
    const { data: channel, error: channelError } = await supabase
      .from('channels')
      .insert([{
        name: channelData.name,
        slug: slug,
        description: channelData.description || null,
        cover_image: channelData.cover_image || null,
        channel_type: channelData.channel_type || 'interest',
        privacy: channelData.privacy || 'public',
        location_city: channelData.location_city || null,
        location_region: channelData.location_region || null,
        tags: channelData.tags || [],
        organization_id: channelData.organization_id || null,
        created_by: channelData.created_by,
        is_active: true,
        is_featured: channelData.is_featured || false,
        is_platform_created: channelData.is_platform_created || false
      }])
      .select()
      .single();

    if (channelError) throw channelError;

    // Insert creator as owner in channel_members
    const { error: memberError } = await supabase
      .from('channel_members')
      .insert([{
        channel_id: channel.id,
        user_uuid: channelData.created_by,
        role: 'owner',
        status: 'active'
      }]);

    if (memberError) throw memberError;

    return channel;
  } catch (error) {
    console.error('Error creating channel:', error);
    throw error;
  }
};

/**
 * Search users for the owner selector in the create channel modal
 * @param {string} searchQuery - Search term (nickname, handle, or email)
 */
export const searchUsers = async (searchQuery) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('uuid, nickname, handle, profile_img, email')
      .or(`nickname.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};
